package com.macrofocus.high_d.table

import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleListProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.units.Quantity
import com.macrofocus.high_d.axis.group.AxisGroupModel
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import mui.x.DataGrid
import mui.x.GridColDef
import mui.x.GridColumnHeaderParams
import mui.x.GridRenderCellParams
import org.mkui.component.CPComponent
import org.mkui.visual.Visual
import org.molap.dataframe.DataFrame
import org.molap.dataframe.DataFrameEvent
import org.molap.dataframe.DataFrameListener
import org.molap.dataframe.SortOrder
import react.*

actual class DefaultTable<R, C, V> : AbstractTable<R, C, V>(), CPComponent {
    var dataFrame: ShowFilteredDataFrame<R, C, V>? = null
    var visual: Visual<R, C>? = null
    var axisGroupModel: AxisGroupModel<R, C>? = null

    val rows = SimpleListProperty(emptyArray<RowData>())
    val columns = SimpleProperty(emptyMap<C,Column>())

    private val renderers = HashMap<C, ((params: GridRenderCellParams) -> ReactElement<*>)>()
    private val headerRenderers = HashMap<C, ((params: GridColumnHeaderParams) -> ReactElement<*>)>()
    private val formats = HashMap<C, CPFormat<*>>()
    private val widths = HashMap<C, Int>()
    private val minWidths = HashMap<C, Int>()
    private val maxWidths = HashMap<C, Int>()
    private val sortingOrders = HashMap<C, Array<String?>>()

    var filter: ReactElement<*>? = null

    override val nativeComponent : FC<*> = FC<Props> {
        var rowsDirty by useState(false)
        var columnsDirty by useState(false)

        useEffect(dataFrame) {
            val listener = object : DataFrameListener<R, C> {
                override fun dataFrameChanged(event: DataFrameEvent<R, C>) {
                    rowsDirty = true
                    columnsDirty = columnsDirty || event.isStructureChanged
                }
            }
            dataFrame?.addDataFrameListener(listener)

            awaitCancellation {
                dataFrame?.removeDataFrameListener(listener)
            }
        }

        val rows: Array<RowData> = useMemo(rowsDirty) {
            if (dataFrame != null) Array<RowData>(dataFrame!!.rowCount) { i ->
                unsafeJso<RowData> {
                    id = dataFrame!!.rowIndex.getKey(i)!!
                }
            } else emptyArray()
        }

        val columns: Array<GridColDef> = useMemo(columnsDirty) {
            if (dataFrame != null) Array<GridColDef>(dataFrame!!.columnCount) { i ->
                val columnKey = dataFrame!!.columnIndex.getKey(i)
                val columnClass = dataFrame!!.getColumnClass(columnKey)
                val column = columns.value[columnKey]
                println("$columnKey : $columnClass")
                unsafeJso<GridColDef> {
                    field = columnKey.toString() // ToDo: toString() as id probably generate issues
                    headerName = if(column != null) column.label else dataFrame!!.getColumnName(columnKey)
                    if (headerRenderers.containsKey(columnKey)) {
                        renderHeader = { params ->
                            val renderer = headerRenderers[columnKey]!!
                            val element = renderer(params)
                            element
                        }
                    }

                    if(sortingOrders.containsKey(columnKey)) {
                        sortingOrder = sortingOrders[columnKey]!!
                    }

//                    width = 200
                    if (formats.containsKey(columnKey)) {
                        val format = formats[columnKey]!!
//                        align = if(columnClass == Quantity::class || columnClass == Double::class) "right" else "left"
                        align = if(format.horizontalAlignment == CPFormat.HorizontalAlignment.Right) "right" else "left"
                        valueFormatter = { value, row, column, apiRef ->
                            val v = dataFrame?.getValueAt(row.id, columnKey).unsafeCast<dynamic>()
                            if (v != null) {
                                val v = format.format(v)
                                if(v != null) v else ""
                            } else {
                                ""
                            }
                        }
                        valueGetter = { value, row, column, apiRef ->
                            if (row != null) {
                                val v = dataFrame?.getValueAt(row.id, columnKey)
                                if(v is Quantity<*>) {
                                    v.amount
                                } else {
                                    v
                                }
                            } else {
                                null
                            }
                        }
                    } else {
                        valueGetter = { value, row, column, apiRef ->
                            if (row != null) {
                                val v = dataFrame?.getValueAt(row.id, columnKey)
                                v
                            } else {
                                "null"
                            }
                        }
                    }
                    if (renderers.containsKey(columnKey)) {
                        renderCell = { params ->
                            val renderer = renderers[columnKey]!!
                            val element = renderer(params)
                            element
                        }
                        display = "flex"
                    }
                    flex = widths[columnKey]
                    width = widths[columnKey]
                    minWidth = minWidths[columnKey]
                    maxWidth = maxWidths[columnKey]
                }
            } else emptyArray()
        }

        val footerElement = useMemo(filter) {
            val f = filter
            CustomFooter.create {
                this.filter = f
            }
        }

        val slots = useMemo {
            unsafeJso<dynamic> {
                footer = { footerElement } // <- function returning same element
            }
        }

        DataGrid {
            this.hideFooter = (filter == null) && rows.size <= 100
            this.columns = columns
            this.rows = rows
            this.asDynamic().density = "compact"
//            this.disableVirtualization = true
//            this.hideFooter = true
//            this.asDynamic().pagination = false
//            this.pageSize = 500
            this.asDynamic().disableColumnMenu = true
            this.asDynamic().slots = slots
        }
    }

    actual override fun setModel(
        dataFrame: ShowFilteredDataFrame<R, C, V>?,
        visual: Visual<R, C>?,
        axisGroupModel: AxisGroupModel<R, C>?
    ) {
        this.dataFrame = dataFrame
        this.visual = visual
        this.axisGroupModel = axisGroupModel
    }

    actual override fun getSortedDataFrame(): DataFrame<R, C, V>? {
        TODO("getSortedDataFrame() not yet implemented")
    }

    actual override fun adjustColumns(columnHeaderIncluded: Boolean, dynamicAdjustment: Boolean) {
        TODO("adjustColumns() not yet implemented")
    }

    actual override fun setFrozenColumnCount(columnCount: Int) {
        TODO("setFrozenColumnCount() not yet implemented")
    }

    actual override fun reset() {
        TODO("reset() not yet implemented")
    }

    actual override fun setShowFiltered(showFiltered: MutableProperty<Boolean>) {
        super.setShowFiltered(showFiltered)
    }

    actual override fun setColumnResizePolicy(policy: Table.ColumnResizePolicy?) {
        TODO("setColumnResizePolicy() not yet implemented")
    }

    actual override fun setDetailsFactory(alwaysVisible: Boolean, factory: Table.DetailsFactory<R>?) {
        TODO("setDetailsFactory() not yet implemented")
    }

    actual override fun setCellRenderer(column: C, renderer: Table.CellRenderer<R, C, V>?) {
        TODO("setCellRenderer)) not yet implemented")
    }

    fun setGridCellRenderer(column: C, renderer: ((params: GridRenderCellParams) -> ReactElement<*>)?) {
        if(renderer != null) {
            renderers.put(column, renderer)
        } else {
            renderers.remove(column)
        }
    }

    fun setHeaderRenderer(column: C, renderer: ((params: GridColumnHeaderParams) -> ReactElement<*>)?) {
        if(renderer != null) {
            headerRenderers.put(column, renderer)
        } else {
            headerRenderers.remove(column)
        }
    }

    actual override fun setLabel(column: C, label: String?) {
        val c: Column = columns.value.getOrElse(column, { Column(label) })
        c.label = label
        columns.value = columns.value.toMutableMap().apply { put(column, c) }
    }

    fun setWidth(column: C, width: Int?) {
        if(width != null) {
            widths.put(column, width)
        } else {
            widths.remove(column)
        }
    }

    fun setWidths(column: C, width: Int?, min: Int?, max: Int?) {
        if(width != null) {
            widths.put(column, width)
        } else {
            widths.remove(column)
        }
        if(min != null) {
            minWidths.put(column, min)
        } else {
            minWidths.remove(column)
        }
        if(max != null) {
            maxWidths.put(column, max)
        } else {
            maxWidths.remove(column)
        }
    }

    actual override fun setFormat(column: C, format: CPFormat<Any?>?) {
        if(format != null) {
            formats.put(column, format)
        } else {
            formats.remove(column)
        }
    }

    actual override fun setSortKey(column: C, order: SortOrder?) {
        TODO("setSortKey() not yet implemented")
    }

    /**
     * By default, the sort order cycles between these three different modes:
     * ['asc', 'desc', null]
     */
    fun setSortingOrder(column: C, orders: Array<String?>) {
        sortingOrders[column] = orders
    }

    actual override fun setHidable(column: C, hidable: Boolean) {
        TODO("setHidable() not yet implemented")
    }

    actual override fun setColumnVisible(column: C, visible: Boolean) {
//        val col = columns.value[column] ?: Column()
//        col.visible = visible
//        columns.value = columns.value.toMutableMap().apply { put(column, col) }
    }
}